// Thin API client. The session token lives in sessionStorage (cleared when the
// tab closes). Moving to httpOnly cookie sessions is a hardening item.

export interface LoginResponse {
  token: string;
  scope: "full" | "enroll";
  role: "admin" | "trader" | "recon" | "viewer";
  must_setup_totp: boolean;
  must_change_password: boolean;
}

export interface PublicUser {
  id: string;
  email: string;
  displayName: string;
  role: "admin" | "trader" | "recon" | "viewer";
  status: "active" | "disabled";
  totpEnabled: boolean;
  createdAt: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

const TOKEN_KEY = "otc.session";

export const session = {
  get token(): string | null {
    return sessionStorage.getItem(TOKEN_KEY);
  },
  set(token: string) {
    sessionStorage.setItem(TOKEN_KEY, token);
  },
  clear() {
    sessionStorage.removeItem(TOKEN_KEY);
  },
};

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      "content-type": "application/json",
      ...(session.token ? { authorization: `Bearer ${session.token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = (data as { code?: string }).code ?? (data as { message?: { code?: string } }).message?.code ?? "ERROR";
    const message = typeof data.message === "string" ? data.message : (data.message?.message ?? code);
    throw new ApiError(res.status, code, message);
  }
  return data as T;
}

export const api = {
  login: (email: string, password: string, totpCode?: string) =>
    request<LoginResponse>("POST", "/auth/login", { email, password, ...(totpCode ? { totp_code: totpCode } : {}) }),
  logout: () => request<{ ok: true }>("POST", "/auth/logout"),
  me: () => request<{ user_id: string; role: PublicUser["role"]; scope: "full" | "enroll" }>("GET", "/auth/me"),
  changePassword: (current: string, next: string) =>
    request<{ ok: true }>("POST", "/auth/password", { current_password: current, new_password: next }),
  totpSetup: () => request<{ otpauth_url: string; secret: string }>("POST", "/auth/totp/setup"),
  totpActivate: (code: string) => request<{ ok: true }>("POST", "/auth/totp/activate", { code }),
  usersList: () => request<{ users: PublicUser[] }>("GET", "/users"),
  userCreate: (email: string, displayName: string, role: PublicUser["role"]) =>
    request<{ user: PublicUser; temporary_password: string }>("POST", "/users", { email, display_name: displayName, role }),
  userSetRole: (id: string, role: PublicUser["role"]) => request<{ user: PublicUser }>("PATCH", `/users/${id}/role`, { role }),
  userSetStatus: (id: string, status: PublicUser["status"]) => request<{ user: PublicUser }>("PATCH", `/users/${id}/status`, { status }),
};
